// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import { escapeHtml } from '../utils/format';
import { glossaryTerm } from './glossaryTip';

interface AboutSource {
  name: string;
  url: string;
  modified: string;
  generatedAt: string;
}

export class AboutModal {
  private el: HTMLDivElement;

  constructor(host: HTMLElement) {
    this.el = document.createElement('div');
    this.el.className = 'modal';
    this.el.setAttribute('role', 'dialog');
    this.el.setAttribute('aria-modal', 'true');
    this.el.innerHTML = `
      <div class="modal-card">
        <header>
          <h3>About Machinery of Government (AU)</h3>
          <button class="close" type="button" aria-label="Close" style="background:transparent;border:1px solid rgba(255,255,255,0.25);color:white;width:28px;height:28px;border-radius:4px;font-size:1.4rem;line-height:1;">×</button>
        </header>
        <div class="body" id="about-body">…</div>
      </div>
    `;
    host.appendChild(this.el);
    this.el.querySelector('.close')!.addEventListener('click', () => this.close());
    this.el.addEventListener('click', (ev) => {
      if (ev.target === this.el) this.close();
    });
    document.addEventListener('keydown', (ev) => {
      if (ev.key === 'Escape') this.close();
    });
  }

  setSource(src: AboutSource): void {
    const body = this.el.querySelector<HTMLDivElement>('#about-body')!;
    body.innerHTML = `
      <p><strong>Machinery of Government (AU)</strong> is an interactive explorer for the Australian Government's full structure of departments, agencies, statutory bodies, advisory committees, government companies and joint ventures.</p>

      <h4>Where the data comes from</h4>
      <p>Every entity on this site is sourced from the ${glossaryTerm('agor', 'Australian Government Organisations Register')} (AGOR), the official register maintained by the Department of Finance and published quarterly through directory.gov.au.</p>
      <p>Snapshot: <strong>${escapeHtml(src.name)}</strong> (file modified ${escapeHtml(src.modified.slice(0, 10))}). Last regenerated by this site: ${escapeHtml(src.generatedAt.slice(0, 10))}. The data pipeline runs weekly and automatically pulls the latest snapshot from <a href="${escapeHtml(src.url)}" target="_blank" rel="noopener">data.gov.au</a>.</p>

      <h4>How the data is structured</h4>
      <p>Each entity has a portfolio (the lead minister's policy area), a ${glossaryTerm('pgpa-act', 'PGPA Act')} classification, a type of body, and where applicable a parent organisation. The ${glossaryTerm('materiality', 'materiality flag')} is set by the Department of Finance for entities with the largest financial or staffing footprint.</p>

      <h4>The Cabinet view</h4>
      <p>Ministerial assignments are sourced from the Second Albanese Ministry list published by the Department of the Prime Minister and Cabinet on 13 May 2025. This is curated by hand and will be refreshed on each reshuffle.</p>

      <h4>Caveats</h4>
      <p>AGOR is comprehensive but lags real-world changes by up to a quarter — newly-created or abolished bodies may not appear until the next quarterly release. Staffing and budget data are not included in the AGOR CSV itself; for figures consult the relevant entity's Annual Report (linked on each entity's detail panel).</p>

      <p style="margin-top:1.5rem;color:var(--text-tertiary);font-size:var(--font-size-xs);">Inspired by <a href="https://machineryofgovernment.uk" target="_blank" rel="noopener">machineryofgovernment.uk</a>.</p>
    `;
  }

  open(): void {
    this.el.classList.add('open');
  }
  close(): void {
    this.el.classList.remove('open');
  }
}
